import React from "react";
import { Box, ClickAwayListener, Grow, Popper, PopperPlacementType, SxProps, Theme } from "@mui/material";
import { useToggleWithAnchor } from "src/theme-mui/components/popper/hooks/use-toggle-with-anchor";
import { PopoverButton } from "./popover-button";

const popperModifiers = [
    { name: "flip", enabled: true },
    { name: "offset", options: { offset: [0, 8] } },
];

type PopperWrapperProps = {
    children: React.ReactNode;
    buttonProps: {
        title: string;
        badgeInfo: string;
    };
    id?: string;
    bodyTestId?: string;
    buttonTestId?: string;
    popperSx?: SxProps<Theme>;
    boxSx?: SxProps<Theme>;
    placement?: PopperPlacementType;
    modifiers?: typeof popperModifiers;
};

export const PopperWrapper = ({
    children,
    buttonProps,
    popperSx,
    boxSx,
    id,
    placement = "bottom",
    modifiers = popperModifiers,
    bodyTestId,
    buttonTestId,
}: PopperWrapperProps) => {
    const { anchorRef, handleClose, handleClick, isOpen } = useToggleWithAnchor();

    const handleClickAway = (event: MouseEvent | TouchEvent) => {
        const clickedInsideAnchor = anchorRef.current?.contains(event.target as Node);
        if (clickedInsideAnchor) {
            return;
        }

        handleClose();
    };

    return (
        <>
            <PopoverButton aria-describedby={id} data-test-id={buttonTestId} onClick={handleClick} ref={anchorRef} isOpen={isOpen} {...buttonProps} />
            <Popper
                id={id}
                transition
                keepMounted
                placement={placement}
                role="dialog"
                anchorEl={anchorRef.current}
                open={isOpen}
                disablePortal
                sx={{
                    zIndex: 1,
                    ...popperSx,
                }}
                modifiers={modifiers}
            >
                {({ placement, TransitionProps }) => (
                    <ClickAwayListener onClickAway={handleClickAway}>
                        <Grow
                            {...TransitionProps}
                            style={{
                                transformOrigin: placement === "top" ? "center bottom" : "center top",
                            }}
                        >
                            <Box
                                sx={{
                                    position: "relative",
                                    backgroundColor: "white",
                                    border: "1px solid",
                                    borderColor: "gray.200",
                                    borderRadius: 1,
                                    overflow: "hidden",
                                    ...boxSx,
                                }}
                                data-test-id={bodyTestId}
                            >
                                {children}
                            </Box>
                        </Grow>
                    </ClickAwayListener>
                )}
            </Popper>
        </>
    );
};
